import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { Button, PopoverHeader, Popover, PopoverBody } from 'reactstrap';
import { connect } from 'react-redux';
import {
  changeStep,
  deselectSeat,
  postThirdPartyReservation,
} from '../../../../actions/index';
import {
  CIVA_BOOKING_PASSENGERS_STEP_INDEX,
  CIVA_BOOKING_STEPS,
  THIRD_PARTY_BOOKING_STEPS,
  THIRD_PARTY_PASSENGERS_STEP_INDEX,
} from '../../../../config/constants';
import { DATE_TIME_FORMAT } from '../../../../config/locale';
import { numberFormatter } from '../../../../utils/number';
import Badge from '../../../common/Badge';
import { tzNormalizeDate } from '../../../../utils/date';
import ReserveToOtherCustomerButton from './ReserveToOtherCustomerButton';

const SelectedSeats = ({
  seatReservations,
  allSeatsSelected,
  dispatchChangeStep,
  dispatchDeselectSeat,
  seatSelectionChangesInProgress,
  isForMine,
  dispatchPostThirdPartyReservation,
}) => {
  const [popoverOpenList, setPopoverOpenList] = useState([]);

  useEffect(() => {
    const newPopoverOpenList = seatReservations.map(() => false);
    if (newPopoverOpenList.length !== popoverOpenList.length) {
      setPopoverOpenList(newPopoverOpenList);
    }
  }, [seatReservations]);

  const toggle = (index) => {
    setPopoverOpenList((prev) => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  };

  const openPopover = (index) => {
    setPopoverOpenList((prev) => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
  };

  const closePopover = (index) => {
    setPopoverOpenList((prev) => {
      const updated = [...prev];
      updated[index] = false;
      return updated;
    });
  };

  const handleNextButtonClick = () => {
    if (isForMine) {
      dispatchPostThirdPartyReservation();
    } else {
      const passengersStep =
        CIVA_BOOKING_STEPS[CIVA_BOOKING_PASSENGERS_STEP_INDEX];
      dispatchChangeStep(passengersStep);
    }
  };

  const reserveToOtherCustomerButtonClick = () => {
    const passengersStep =
      THIRD_PARTY_BOOKING_STEPS[THIRD_PARTY_PASSENGERS_STEP_INDEX];
    dispatchChangeStep(passengersStep);
  };

  const renderSeats = () => {
    if (!seatReservations.length) {
      return (
        <span className="text-muted">
          <i>No hay asientos seleccionados</i>
        </span>
      );
    }

    let total = 0;

    const items = seatReservations.map((seatReservation, index) => {
      const {
        seat: { seatNumber },
        listPrice,
        id: seatReservationId,
        seatId,
        itineraryId,
        itinerary: {
          originName,
          destinationName,
          departureTime,
          arrivalTime,
          serviceName,
        },
      } = seatReservation;

      total += listPrice || 0;

      const formattedDepartureTime = tzNormalizeDate({
        date: departureTime,
        format: DATE_TIME_FORMAT,
      });

      const formattedArrivalTime = tzNormalizeDate({
        date: arrivalTime,
        format: DATE_TIME_FORMAT,
      });

      const amount = listPrice ? numberFormatter({ value: listPrice }) : null;

      let deselectSeatFunction = null;

      if (seatSelectionChangesInProgress.size === 0) {
        deselectSeatFunction = () =>
          dispatchDeselectSeat({
            seatReservationId,
            itineraryId,
            seatId,
          });
      }

      return (
        <li className="list-group-item p-0" key={`td_${index.toString()}`}>
          <ul className="list-group list-group-horizontal">
            <li
              className={`list-group-item border-0 pl-1 pr-1 pt-2 pb-2 ${
                isForMine ? 'text-center w-100' : 'w20p'
              }`}
            >
              <Badge
                className="colorPurple clickable"
                onFocus={() => openPopover(index)}
                onBlur={() => closePopover(index)}
                onMouseEnter={() => openPopover(index)}
                onMouseLeave={() => closePopover(index)}
                onClick={() => openPopover(index)}
                id={`pp_${index}`}
                role="button"
                text={seatNumber}
              />
              <Popover
                placement="bottom"
                isOpen={popoverOpenList[index]}
                target={`pp_${index}`}
                toggle={() => toggle(index)}
              >
                <PopoverHeader>{serviceName}</PopoverHeader>
                <PopoverBody>
                  <p>
                    Origen: {originName}
                    <br />
                    Salida: {formattedDepartureTime}
                  </p>
                  <p>
                    Destino: {destinationName}
                    <br />
                    Llegada: {formattedArrivalTime}
                  </p>
                </PopoverBody>
              </Popover>
            </li>
            {isForMine ? (
              ''
            ) : (
              <>
                <li className="list-group-item border-0 pl-1 pr-1 pt-2 pb-2 text-right w60p">
                  {amount}
                </li>
                <li className="list-group-item border-0 pl-1 pr-1 pt-2 pb-2 text-right w20p">
                  <i
                    className="fa fa-times-circle text-danger clickable"
                    role="button"
                    onClick={deselectSeatFunction}
                    onKeyPress={deselectSeatFunction}
                    tabIndex={0}
                  />
                </li>
              </>
            )}
          </ul>
        </li>
      );
    });

    const footer =
      total !== 0 && !isForMine ? (
        <li className="list-group-item p-0" key="total">
          <ul className="list-group list-group-horizontal">
            <li className="list-group-item border-0 pl-1 pr-1 pt-2 pb-2 w20p">
              &nbsp;
            </li>
            <li className="list-group-item border-0 pl-1 pr-1 pt-2 pb-2 w60p text-right font-weight-bold">
              TOTAL: {numberFormatter({ value: total })}
            </li>
            <li className="list-group-item border-0 pl-1 pr-1 pt-2 pb-2 w20p">
              &nbsp;
            </li>
          </ul>
        </li>
      ) : null;

    return (
      <ul className="list-group list-group-flush">
        {items}
        {footer}
      </ul>
    );
  };

  const renderNextButton = () => {
    const buttonText = isForMine ? 'Reservar' : 'Siguiente';
    return (
      <Button
        className="mt-3"
        disabled={!allSeatsSelected}
        color="primary"
        onClick={handleNextButtonClick}
        size="lg"
      >
        {buttonText} <i className="fa fa-arrow-circle-right" />
      </Button>
    );
  };

  return (
    <div className="mt-4">
      <div className="card">
        <div className="card-body p-3">
          <h5 className="card-title">Asientos</h5>
          <div className="card-text">{renderSeats()}</div>
          <div className={isForMine ? 'text-center' : 'text-right'}>
            {renderNextButton()}
            <ReserveToOtherCustomerButton
              onClick={reserveToOtherCustomerButtonClick}
              allSeatsSelected={allSeatsSelected}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

SelectedSeats.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  seatReservations: PropTypes.array,
  allSeatsSelected: PropTypes.bool.isRequired,
  dispatchChangeStep: PropTypes.func.isRequired,
  dispatchDeselectSeat: PropTypes.func.isRequired,
  seatSelectionChangesInProgress: PropTypes.instanceOf(Immutable.Set)
    .isRequired,
  isForMine: PropTypes.bool.isRequired,
  dispatchPostThirdPartyReservation: PropTypes.func.isRequired,
};

SelectedSeats.defaultProps = { seatReservations: [] };

const mapStateToProps = ({ BookingUnit, authentication }) => {
  const numPassengers = BookingUnit.Booking.getIn([
    'search',
    'query',
    'numPassengers',
  ]);

  const seatReservations = BookingUnit.Booking.getIn([
    'seats',
    'seatReservations',
  ]).toJS();

  const seatSelectionChangesInProgress = BookingUnit.Booking.getIn([
    'seats',
    'seatSelectionChangesInProgress',
  ]);

  let allSeatsSelected = false;

  // validation when numPassengers is null
  // there is no limit of seats
  if (
    !numPassengers &&
    seatReservations.length >= 1 &&
    !seatSelectionChangesInProgress.size
  ) {
    allSeatsSelected = true;
    // validation when there is a numPassengers
    // there is a limit of seats
  } else if (
    numPassengers &&
    parseInt(numPassengers, 10) === seatReservations.length &&
    !seatSelectionChangesInProgress.size
  ) {
    allSeatsSelected = true;
  }
  return {
    seatReservations,
    allSeatsSelected,
    seatSelectionChangesInProgress,
    isForMine: !!authentication.get('user').salesSessionUserId,
  };
};

const mapDispatchToProps = {
  dispatchChangeStep: changeStep,
  dispatchDeselectSeat: deselectSeat,
  dispatchPostThirdPartyReservation: postThirdPartyReservation,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectedSeats);
